import { SalesforceContactId } from '@/shared/types';
import { inject, Injectable } from '@angular/core';
import { UserRoleUid } from '@enums';
import { UserClaims } from '@okta/okta-auth-js';
import { AuthService, KeplerSfdcCase, KeplerSfdcService, KeplerSfdcUserDetail } from '@services';
import { catchError, filter, map, Observable, of, ReplaySubject, switchMap, tap } from 'rxjs';

export interface MyCase {
    id: string;
    case?: string;
    subject?: string;
    status?: string;
    updatedDate?: string;
}

@Injectable({
    providedIn: 'root',
})
export class UserService {
    private authService = inject(AuthService);

    private _isAuthenticated = new ReplaySubject<boolean>(1);
    private _claims$ = new ReplaySubject<UserClaims>(1);
    private _sfdcUserDetail$ = new ReplaySubject<KeplerSfdcUserDetail>(1);
    private _sfdcContactId: SalesforceContactId | null;
    private _userRole$ = new ReplaySubject<UserRoleUid>(1);
    private _isEmployee$ = new ReplaySubject<boolean>(1);
    private _isCssEmployee$ = new ReplaySubject<boolean>(1);

    static CSS_EVP_ID = 'Scott Cravotta';

    private keplerSfdcService = inject(KeplerSfdcService);

    constructor() {
        this.authService.isAuthenticated$
            .pipe(
                tap((isAuthenticated: boolean) => {
                    this._isAuthenticated.next(isAuthenticated);
                    console.debug('Authenticated: %o', isAuthenticated);
                }),
                filter((isAuthed) => {
                    if (!isAuthed) {
                        // Some properties still need default values for unauth'ed state, but not all properties. Choose wisely here.
                        this._userRole$.next(UserRoleUid.PUBLIC);
                        this._isEmployee$.next(false);
                        this._isCssEmployee$.next(false);
                    }

                    return isAuthed === true; // purposely verbose for clairty
                }),
                filter((isAuthenticated) => isAuthenticated !== undefined),
                switchMap(() => this.authService.claims$),
                tap((claims: UserClaims) => this._claims$.next(claims)),
                switchMap(() =>
                    this.keplerSfdcService.sfdcUserDetail$.pipe(
                        catchError((error) => {
                            console.error('Error fetching SFDC user detail:', error);
                            return of(null); // proceed onward
                        }),
                    ),
                ),
                tap((sfdcUserDetail) => {
                    this._sfdcUserDetail$.next(sfdcUserDetail);

                    if (sfdcUserDetail) {
                        this._sfdcContactId = sfdcUserDetail.Contacts?.[0]?.Id ?? null;
                        const userRole = this.determineUserRole(sfdcUserDetail);
                        this._userRole$.next(userRole);
                        this._isEmployee$.next(
                            userRole === UserRoleUid.EMPLOYEE ||
                                userRole === UserRoleUid.CSM_TAM ||
                                sfdcUserDetail?.User_Groups?.includes('CSDTTesters') || // CSDT Employees
                                this.isCssEmployee(sfdcUserDetail),
                        );
                        this._isCssEmployee$.next(this.isCssEmployee(sfdcUserDetail));
                    } else {
                        // Set default values when SFDC user detail is not available
                        this._userRole$.next(UserRoleUid.AUTHENTICATED);
                        this._isEmployee$.next(false);
                        this._isCssEmployee$.next(false);
                    }
                }),
            )
            .subscribe();
    }

    /**
     * Determines the user's role using the the SFDC API UserDetail response data
     * @param {string} sfUserType UserType returned from Salesforce
     */
    private determineUserRole(data: KeplerSfdcUserDetail): UserRoleUid {
        const profileName = data.Profile?.Name;
        if (profileName) {
            if (this.isCsmOrTamUser(profileName)) {
                return UserRoleUid.CSM_TAM;
            }
        }

        const sfUserType = data.User?.UserType;
        switch (sfUserType) {
            case 'PowerPartner':
                return UserRoleUid.PARTNER;
            case 'Standard':
                return UserRoleUid.EMPLOYEE;
            case 'PowerCustomerSuccess':
                return UserRoleUid.CUSTOMER;
            default:
                return UserRoleUid.AUTHENTICATED;
        }
    }

    /**
     * Indicates whether the given SFDC User Profile name is for a Customer Success Manager (CSM) or a Technical Account Manager (TAM) user.
     * @param userProfileName the current user's SFDC User Profile name
     * @returns boolean
     */
    private isCsmOrTamUser(userProfileName: string): boolean {
        // Possible profiles are "GEN - Sales CSM" and "Customer Care TAM LEX".
        // We don't include the full name of the "GEN - Sales CSM" profile to avoid unicode character comparison errors with the "-" character.
        return userProfileName.includes('Sales CSM') || userProfileName.includes('Customer Care TAM LEX');
    }

    /** @returns true when the user is authenticated */
    get isAuthenticated$(): Observable<boolean> {
        return this._isAuthenticated.asObservable();
    }

    /** @returns user oauth claims */
    get claims$(): Observable<UserClaims> {
        return this._claims$.asObservable();
    }

    /** @returns the user first/last name as initials */
    get initials$(): Observable<string> {
        return this.claims$.pipe(
            map((claims) => {
                return (claims?.given_name?.slice(0, 1) || '') + (claims?.family_name?.slice(0, 1) || '');
            }),
        );
    }

    /** @returns all salesforce data for this user (made available via our REST integration, not actually every single salesforce field) */
    get sfdcUserDetail$(): Observable<KeplerSfdcUserDetail> {
        return this._sfdcUserDetail$.asObservable();
    }

    /** @returns shortcut for {@link sfdcUserDetail$} to get this user's salesforce contact ID. This can be considered a salesforce user's primary key */
    get sfdcContactId(): SalesforceContactId {
        return this._sfdcContactId;
    }

    /** @returns the user's GKN role, which is based on salesforce user type, profile name, and other possible information */
    get userRole$(): Observable<UserRoleUid> {
        return this._userRole$.asObservable();
    }

    get isEmployee$(): Observable<boolean> {
        return this._isEmployee$.asObservable();
    }

    get isCssEmployee$(): Observable<boolean> {
        return this._isCssEmployee$.asObservable();
    }

    /**
     * @returns true when this user is a Customer Success employee via the Executive VP assigned to the user.
     */
    private isCssEmployee(data?: KeplerSfdcUserDetail): boolean {
        return data?.User?.EVP__c === UserService.CSS_EVP_ID;
    }

    get cases$(): Observable<MyCase[]> {
        return this.sfdcUserDetail$.pipe(
            map((sfUserDetail) => {
                return (
                    sfUserDetail?.Cases?.map((caseRecord: KeplerSfdcCase) => ({
                        id: caseRecord.Id,
                        case: caseRecord.CaseNumber,
                        subject: caseRecord.Subject,
                        status: caseRecord.Status,
                        updatedDate: caseRecord.LastModifiedDate,
                    })) || []
                );
            }),
        );
    }
}
